import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  DialogPlugin,
  Dropdown,
  Form,
  Input,
  Link,
  MessagePlugin,
  Space,
  Table,
  Tag,
} from 'tdesign-react';
import { connect } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchGeoList as fetchClusterGeoList } from '@/slices/clusterSlice';
import {
  deleteGeo,
  getGeoChartData,
  getGeoOutList,
  getPartionGeoChartData,
  getPartitionGeoOutList,
} from '@/services/topicService';
import useTable from '@/hooks/useTable';
import {
  renderByteRate,
  renderDateTime,
  renderNum,
  renderNumRate,
  renderTimeUnitFromSecond,
} from '@/utils/common';
import ColTip from '@/components/Tip';
import SvgIcon from '@/components/SvgIcon';
import BoardModal from '@/components/Cluster/BoardModal';
import ModalHeader from '@/components/Header/Modal';
import PopupLink from '@/components/Cluster/PopupLink';

import PermWrapper from '@/components/PermWrapper';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';
import ModalForm from './ModalForm';

const { FormItem } = Form;

function ToRemote({ overview, commonProps, fetchClusterGeoList, closeDrawer }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const clusterName = searchParams.get('name');
  const [visible, setVisible] = useState(false);
  const [boardVisible, setBoardVisible] = useState(false);
  const [currentRemoteCluster, setCurrentRemoteCluster] = useState();

  const confirmBtn = useDisableButton({ content: '确定' });
  const isPartition = overview.topicPartitionCount > 0;

  const columns = [
    {
      title: '远端集群',
      colKey: 'replicationCluster',
      width: 180,
      fixed: 'left',
      cell: ({ row }) => (
        <PopupLink
          tip='跳转远端Topic'
          onClick={() => {
            closeDrawer();
            navigate(
              `/cluster/topic/list?name=${row.replicationCluster}&topic=${commonProps.topic}`,
            );
          }}
        >
          {row.replicationCluster}
        </PopupLink>
      ),
    },
    {
      title: '是否启动',
      colKey: 'isConnected',
      width: 120,
      cell: ({ row }) => (row.isConnected ? '是' : '否'),
    },
    {
      title: '写出速率',
      colKey: 'rateOut',
      width: 120,
      cell: renderNumRate,
    },
    {
      title: '写出吞吐',
      colKey: 'byteOut',
      width: 120,
      cell: renderByteRate,
    },
    {
      title: '落后条数',
      colKey: 'backlog',
      width: 120,
      cell: renderNum,
    },
    {
      title: '落后延迟',
      width: 120,
      colKey: 'delayInSeconds',
      cell: renderTimeUnitFromSecond,
    },
    {
      colKey: 'getTime',
      cell: renderDateTime,
      sorter: true,
      title: <ColTip content='非实时，有分钟级延迟'>采集时间</ColTip>,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space size='small'>
          <Icon type='presentation-chart-line' size={18} onClick={() => showChart(row)} />
          <PermWrapper resource='pulsar:topic:list:GEOConfig'>
            <Dropdown
              options={[
                {
                  value: 'delete',
                  content: (
                    <Link theme='danger' hover='color'>
                      删除
                    </Link>
                  ),
                },
              ]}
              onClick={({ value }) => onSelect(value, row)}
            >
              <span>
                <Icon size={18} type='dots-vertical' />
              </span>
            </Dropdown>
          </PermWrapper>
        </Space>
      ),
      width: 100,
    },
  ];

  const onSelect = (value, row) => {
    switch (value) {
      case 'delete': // 删除
        {
          const dialog = DialogPlugin.confirm({
            header: '提示',
            theme: 'warning',
            body: (
              <>
                确定删除？<Tag>{row.replicationCluster}</Tag>
              </>
            ),
            confirmBtn,
            onClose: () => dialog.hide(),
            onConfirm: async () => {
              try {
                dialog.update({
                  confirmBtn: { loading: true, content: '确定' },
                });
                await deleteGeo({
                  clusterName,
                  replicationClusterName: row.replicationCluster,
                  topic: commonProps.topic,
                });
                MessagePlugin.success('操作成功！');
                dialog.hide();
                doRefresh();
              } catch (error) {
                console.trace(error);
                throw error;
              } finally {
                dialog.update({
                  confirmBtn: { loading: false, content: '确定' },
                });
              }
            },
          });
        }
        break;

      default:
        break;
    }
  };

  const showChart = (row) => {
    setBoardVisible(true);
    setCurrentRemoteCluster(row.replicationCluster);
  };

  const loadData = useMemoizedFn(async (params) => {
    const filter = params?.filter;
    const request = isPartition ? getPartitionGeoOutList : getGeoOutList;
    const { data } = await request(commonProps);

    if (filter) {
      setData(data.filter((item) => item.replicationCluster.indexOf(filter) > -1));
    } else {
      setData(data);
    }
  });

  const { setData, config, header, doSearch, doRefresh, data } = useTable({
    columns,
    onSearch: loadData,
    operators: (
      <>
        <Link
          hover='color'
          theme='default'
          style={{ margin: 6 }}
          onClick={() => {
            setBoardVisible(true);
          }}
        >
          <Icon type='presentation-chart-line' size={18} />
        </Link>
        <PermWrapper resource='pulsar:topic:list:GEOCreate'>
          <Button onClick={() => setVisible(true)}>添加GEO</Button>
        </PermWrapper>
      </>
    ),
    search: (
      <Space align='center'>
        <Title>同步远端</Title>
        <FormItem name='filter'>
          <Input placeholder='请输入GEO远端集群' />
        </FormItem>
      </Space>
    ),
  });

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  useEffect(() => {
    fetchClusterGeoList(clusterName);
  }, [clusterName, fetchClusterGeoList]);

  const handleClose = (refresh) => {
    setVisible(false);
    setBoardVisible(false);
    setCurrentRemoteCluster(null);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <ModalForm
        isPartition={isPartition}
        visible={visible}
        handleClose={handleClose}
        topic={commonProps.topic}
        clusters={data}
      />
      <BoardModal
        visible={boardVisible}
        handleClose={handleClose}
        header={
          <ModalHeader tags={[clusterName, commonProps.topic, currentRemoteCluster]}>
            监控-GEO
          </ModalHeader>
        }
        getChartsData={(params) => {
          if (isPartition) {
            return getPartionGeoChartData({
              ...params,
              clusterName,
              topic: commonProps.topic,
              remoteCluster: currentRemoteCluster,
            });
          } else {
            return getGeoChartData({
              ...params,
              clusterName,
              topic: commonProps.topic,
              remoteCluster: currentRemoteCluster,
            });
          }
        }}
      />
    </Root>
  );
}

export default connect(
  (state) => ({
    overview: state.topic.detailOverview,
  }),
  {
    fetchClusterGeoList,
  },
)(ToRemote);

const Root = styled.div`
  margin-top: 28px;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
  font-size: 20px;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: bold;
  padding-left: 4px;
`;
